import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import {
  panelHairline,
  panelTitleSx,
  roundedPanelSx,
  touchTargetSx,
  workspaceSwitchSx,
} from "../../overviewPanel";
import { useCompanyInsiderTrading } from "../hooks/useCompanyInsiderTrading";
import {
  DEFAULT_INSIDER_PERIOD_MONTHS,
  INSIDER_PERIOD_OPTIONS,
  insiderPeriodForMonths,
} from "../hooks/insiderPeriods";
import { useCompanyPriceEod } from "../hooks/useCompanyPriceEod";
import ChartCardWrapper from "./shared/ChartCardWrapper";
import type { InsiderTradingData } from "../hooks/useInsiderTrading";
import { InsiderActivityChart } from "./InsiderActivityChart";
import { InsiderTransactionsTable } from "./InsiderTransactionsTable";
import { compactShareCount, getInsiderActivity } from "./insiderActivityData";
import SubscribeButton from "../../../Notifications/SubscribeButton";

const EMPTY_TRADES: InsiderTradingData[] = [];

const LOAD_ERROR = "Unable to load insider transactions.";

/** The error codes `GET /insider-trading/company` returns besides a plain failure. */
const INSIDER_ERROR_MESSAGES = new Map<string, string>([
  ["MEMBERSHIP_REQUIRED", "Insider activity is available to members."],
  ["RATE_LIMITED", "You've loaded a lot of insider activity in a short time. Try again in a minute."],
  ["INSIDER_DATA_BUSY", "Insider data is busy right now. Try again shortly."],
]);

// Read structurally: importing axios here would stop jest collecting this file.
const insiderErrorCode = (error: unknown): string => {
  const code = (error as { response?: { data?: { error?: unknown } } } | null)?.response?.data?.error;
  return typeof code === "string" ? code : "";
};

interface InsiderTradesTabProps {
  ticker: string;
}

const InsiderActivityContent: React.FC<InsiderTradesTabProps> = ({
  ticker,
}) => {
  const theme = useTheme();
  const [period, setPeriod] = useState(DEFAULT_INSIDER_PERIOD_MONTHS);
  const [selection, setSelection] = useState<{
    trade: InsiderTradingData;
  } | null>(null);
  const to = dayjs().format("YYYY-MM-DD");
  const from = dayjs().subtract(period, "month").format("YYYY-MM-DD");
  // Company-level: every share class together, already consolidated and cut
  // to the period by the server.
  const company = useCompanyInsiderTrading(
    ticker,
    insiderPeriodForMonths(period),
  );
  // While a new period loads, the hook lends the previous period's answer for
  // the company-level facts in the header. Its rows are another period's.
  const isLoadingPeriod = company.isLoading || company.isPlaceholderData;
  const trades = isLoadingPeriod
    ? EMPTY_TRADES
    : (company.data?.trades ?? EMPTY_TRADES);
  const showsAllShareClasses = (company.data?.listedSymbols.length ?? 0) > 1;
  let title = "Insider activity";
  if (showsAllShareClasses)
    title = `${company.data?.companyName ?? ticker} insider activity — all share classes`;
  // Alerts exist only where the SERVER says the company can be followed. Never
  // inferred from the rows below: a followable company can have an empty tab
  // (SHOP), and a tab can show another CIK's fallback history (CLBK).
  const canSubscribe = company.data?.canSubscribe === true;
  const prices = useCompanyPriceEod(ticker, from, to);
  const activity = useMemo(
    () => getInsiderActivity(trades, prices.data?.data ?? [], from, to),
    [trades, prices.data, from, to],
  );
  const isDark = theme.palette.mode === "dark";
  const net = activity.bought - activity.sold;
  let netColor = theme.palette.text.secondary;
  if (net > 0) netColor = theme.palette.success.main;
  if (net < 0) netColor = theme.palette.error.main;
  const netPrefix = net > 0 ? "+" : "";
  const metrics = [
    {
      label: "Shares bought",
      value: compactShareCount(activity.bought),
      color: theme.palette.text.primary,
    },
    {
      label: "Shares sold",
      value: compactShareCount(activity.sold),
      color: theme.palette.text.primary,
    },
    {
      label: "Net shares",
      value: `${netPrefix}${compactShareCount(net)}`,
      color: netColor,
    },
  ];
  // A failed background refetch keeps showing the filings already loaded.
  const initialError = company.isError && !company.data;
  const errorCode = insiderErrorCode(company.error);
  const canRetry = errorCode !== "MEMBERSHIP_REQUIRED";
  const canShowActivity = !isLoadingPeriod && !initialError;
  const isIncomplete = canShowActivity && company.data?.complete === false;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        pb: 5,
        "& > .MuiCard-root": {
          ...roundedPanelSx(isDark),
          p: 0,
        },
        "& > .MuiCard-root > .MuiCardContent-root": {
          p: 2,
        },
      }}
    >
      <ChartCardWrapper renderCard>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            columnGap: 1.5,
            rowGap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              flex: "1 1 0",
              minWidth: 0,
            }}
          >
            <Typography
              component="h2"
              sx={{
                ...panelTitleSx,
                fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                color: "text.primary",
                borderLeft: 2,
                borderColor: "primary.main",
                pl: 1,
                lineHeight: 1.5,
              }}
            >
              {title}
            </Typography>
            <Tooltip title="Highlights larger insider buys and sells in the selected period.">
              <IconButton
                size="small"
                aria-label="About notable insider activity"
                sx={{
                  color: "text.secondary",
                  width: 28,
                  height: 28,
                  ...touchTargetSx,
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: 2,
                  },
                }}
              >
                <InfoOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          {canSubscribe && (
            // Beside the period switch; on xs it drops onto its own row, on
            // the right under the switch (Ben, 2026-10-05). The button renders
            // nothing when this user cannot have it (native without access),
            // and an empty wrapper must not hold a row.
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-end", sm: "flex-start" },
                order: { xs: 3, sm: 0 },
                flexBasis: { xs: "100%", sm: "auto" },
                "&:empty": { display: "none" },
              }}
            >
              <SubscribeButton
                topic="insider_trades"
                entityKind="ticker"
                entityKey={ticker}
                entityRef={company.data?.companyCik ?? null}
                label={company.data?.companyName}
                source="insiders_tab"
              />
            </Box>
          )}
          <ToggleButtonGroup
            exclusive
            value={period}
            aria-label="Insider activity period"
            onChange={(_, value: number | null) => {
              if (value === null) return;
              setPeriod(value);
              setSelection(null);
            }}
            sx={{ ...workspaceSwitchSx, flexShrink: 0 }}
          >
            {INSIDER_PERIOD_OPTIONS.map((item) => (
              <ToggleButton key={item.months} value={item.months}>
                {item.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        {initialError && (
          <Alert
            severity="error"
            action={
              canRetry && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void company.refetch()}
                >
                  Retry
                </Button>
              )
            }
          >
            {INSIDER_ERROR_MESSAGES.get(errorCode) ?? LOAD_ERROR}
          </Alert>
        )}
        {isIncomplete && (
          <Typography
            role="status"
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 2 }}
          >
            Some filings couldn't be loaded — showing what we could verify.
          </Typography>
        )}
        {isLoadingPeriod && (
          <Box aria-label="Loading insider transactions">
            <Skeleton width="55%" height={80} />
            <Skeleton variant="rounded" height={340} sx={{ my: 3 }} />
            <Skeleton height={60} />
            <Skeleton height={60} />
          </Box>
        )}
        {canShowActivity && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                mb: 2,
                py: 1.5,
                "& > :not(:first-of-type)": {
                  pl: { xs: 1.5, sm: 3 },
                  borderLeft: `1px solid ${panelHairline(isDark)}`,
                },
              }}
            >
              {metrics.map((metric) => (
                <Box key={metric.label} sx={{ minWidth: 0 }}>
                  <Typography
                    component="h3"
                    sx={{
                      fontSize: { xs: "0.625rem", sm: "0.6875rem" },
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "text.secondary",
                      mb: 0.5,
                    }}
                  >
                    {metric.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "1.375rem",
                        sm: "1.625rem",
                        md: "1.75rem",
                      },
                      lineHeight: 1.2,
                      fontWeight: 600,
                      letterSpacing: "-0.025em",
                      color: metric.color,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {metric.value}
                  </Typography>
                </Box>
              ))}
            </Box>
            {prices.isError && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => void prices.refetch()}
                  >
                    Retry
                  </Button>
                }
              >
                Closing prices could not be loaded. Insider share volumes are
                still shown.
              </Alert>
            )}
            {!prices.isLoading &&
              !prices.isError &&
              activity.pricePoints.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  No closing prices available for this period.
                </Typography>
              )}
            <InsiderActivityChart
              activity={activity}
              from={from}
              to={to}
              isLoading={prices.isLoading}
              onSelectTrade={(trade) => setSelection({ trade })}
            />
          </>
        )}
      </ChartCardWrapper>
      {canShowActivity && (
        <ChartCardWrapper renderCard>
          <InsiderTransactionsTable
            key={period}
            trades={activity.trades}
            prices={prices.data?.data}
            estimateAwards={company.data?.isAdr === false}
            selection={selection}
          />
        </ChartCardWrapper>
      )}
    </Box>
  );
};

export const InsiderTradesTab: React.FC<InsiderTradesTabProps> = ({
  ticker,
}) => <InsiderActivityContent key={ticker} ticker={ticker} />;
